import type { AxiosInstance } from 'axios';

export const NEIGHBORHOOD_MIN_SEARCH_CHARS = 2;

export interface NeighborhoodPlaceRow {
  place_id: string;
  formatted_address?: string;
}

export interface MapsTextSearchResponse {
  status: string;
  results: NeighborhoodPlaceRow[];
}

export interface NeighborhoodOption {
  id: string;
  name: string;
}

/** Mesma normalização usada no fluxo antigo para dedupe. */
export function normalizeNeighborhoodKey(text: string): string {
  return text
    .split(/[-/]/)
    .join(' ')
    .trim()
    .replace(/\s+/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Primeiro segmento de `formatted_address` (bairro), alinhado ao app antigo. */
export function shortNeighborhoodFromFormatted(formattedAddress: string): string {
  const fa = formattedAddress.trim();
  if (!fa) return '';
  return fa.split(',')[0]?.trim() || fa;
}

export function buildNeighborhoodQuery(search: string, cityName: string): string {
  const s = search.trim();
  if (cityName === 'Recife') {
    return `bairro+${encodeURIComponent(s)}+bairros+da+cidade+do+${encodeURIComponent(cityName)}&type=sublocality&region=BR`;
  }
  return `bairro+${encodeURIComponent(s)}+bairros+de+${encodeURIComponent(cityName)}&type=sublocality&region=BR`;
}

export async function fetchNeighborhoodSuggestions(
  api: AxiosInstance,
  cityName: string,
  search: string
): Promise<NeighborhoodOption[]> {
  const query = buildNeighborhoodQuery(search, cityName);
  const response = await api.get<MapsTextSearchResponse>(`/maps/textsearch?query=${query}`);
  const data = response.data;

  if (data.status !== 'OK' || !Array.isArray(data.results)) {
    return [];
  }

  const withAddress = data.results.filter((p) => p.formatted_address?.trim());

  return withAddress
    .filter((place, index, self) => {
      const short = shortNeighborhoodFromFormatted(place.formatted_address!);
      if (!short) return false;
      const b = normalizeNeighborhoodKey(short);
      return (
        index ===
        self.findIndex((p) => {
          const s = p.formatted_address && shortNeighborhoodFromFormatted(p.formatted_address);
          return s && normalizeNeighborhoodKey(s) === b;
        })
      );
    })
    .map((place) => ({
      id: place.place_id,
      name: shortNeighborhoodFromFormatted(place.formatted_address!),
    }))
    .filter((row) => row.name.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}
